import "./CreateMessage.scss";
import { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import {
  NotificationMessagesTechniciansColumns,
  NotificationMessagesUsersColumns,
} from "../../../SourceData";

const CreateMessage = () => {
  // Form state
  const [formData, setFormData] = useState({
    message: "",
    receiverType: "all", // Default to "all"
    receiverIds: [],
    receiverIdsType: "", // This will be set when needed
  });

  // Message stats
  const [charCount, setCharCount] = useState(0);
  const [pointCount, setPointCount] = useState(1);

  // UI states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedReceivers, setSelectedReceivers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Calculate character count and points
  useEffect(() => {
    const count = formData.message.length;
    setCharCount(count);

    if (count > 160) {
      setPointCount(3);
    } else if (count > 70) {
      setPointCount(2);
    } else {
      setPointCount(1);
    }
  }, [formData.message]);

  // Debounce search term changes
  useEffect(() => {
    const handler = setTimeout(() => {
      // Only update debounced search term if length is 0 or at least 3 characters
      if (searchTerm.length === 0 || searchTerm.length >= 3) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle receiver type change
  const handleReceiverTypeChange = (event) => {
    const value = event.target.value;

    // Reset search term and selected receivers when changing type
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedReceivers([]);

    // Map the UI selection values to API values
    let apiReceiverType = value;
    let receiverIdsType = "";

    // Handle mapping to the desired API format
    if (value === "specific-users") {
      apiReceiverType = "custom";
      receiverIdsType = "user";
      // Reset page when changing type
      setPage(1);
    } else if (value === "specific-technicians") {
      apiReceiverType = "custom";
      receiverIdsType = "technician";
      // Reset page when changing type
      setPage(1);
    }

    setFormData({
      ...formData,
      receiverType: apiReceiverType,
      receiverIds: [], // Reset receiver IDs
      receiverIdsType: receiverIdsType,
    });
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Fetch users - memoized to prevent recreation on each render
  const fetchUsers = useCallback(async () => {
    if (formData.receiverIdsType !== "user") return;

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Only include search term if we have a valid one
      if (debouncedSearchTerm) {
        params.q = debouncedSearchTerm;
      }

      const response = await api.get("/admin/users", { params });

      if (response.data.status === "success") {
        setUsers(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("فشل في جلب المستخدمين. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, formData.receiverIdsType]);

  // Fetch technicians - memoized to prevent recreation on each render
  const fetchTechnicians = useCallback(async () => {
    if (formData.receiverIdsType !== "technician") return;

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Only include search term if we have a valid one
      if (debouncedSearchTerm) {
        params.q = debouncedSearchTerm;
      }

      const response = await api.get("/technicians", { params });

      if (response.data.status === "success") {
        setTechnicians(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
      toast.error("فشل في جلب الفنيين. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, formData.receiverIdsType]);

  // Effect for fetching data when receiverType or page/pageSize changes
  useEffect(() => {
    if (formData.receiverType === "custom") {
      if (formData.receiverIdsType === "user") {
        fetchUsers();
      } else if (formData.receiverIdsType === "technician") {
        fetchTechnicians();
      }
    }
  }, [
    formData.receiverType,
    formData.receiverIdsType,
    fetchUsers,
    fetchTechnicians,
  ]);

  // Effect for responding to debounced search term changes
  useEffect(() => {
    if (formData.receiverType === "custom") {
      // Reset to first page when search term changes
      setPage(1);

      if (formData.receiverIdsType === "user") {
        fetchUsers();
      } else if (formData.receiverIdsType === "technician") {
        fetchTechnicians();
      }
    }
  }, [
    debouncedSearchTerm,
    formData.receiverType,
    formData.receiverIdsType,
    fetchUsers,
    fetchTechnicians,
  ]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Create base payload according to specified format
      const dataToSend = {
        receiverType: formData.receiverType,
        message: formData.message,
      };

      // Add receiver IDs and type if custom receivers are selected
      if (
        formData.receiverType === "custom" &&
        formData.receiverIds.length > 0
      ) {
        dataToSend.receiverIds = formData.receiverIds;
        dataToSend.receiverIdsType = formData.receiverIdsType;
      }

      const response = await api.post("/messages", dataToSend);

      if (response.data.status === "success") {
        toast.success("تم إرسال الرسالة بنجاح");
        // Reset form
        setFormData({
          message: "",
          receiverType: "all",
          receiverIds: [],
          receiverIdsType: "",
        });
        setSelectedReceivers([]);
        setSearchTerm("");
        setDebouncedSearchTerm("");
      } else {
        throw new Error(response.data.message || "فشل في إرسال الرسالة");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(
        error.response?.data?.message ||
          "فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle selection of receivers in the DataGrid
  const handleSelectionModelChange = (selectionModel) => {
    const dataArray = formData.receiverIdsType === "user" ? users : technicians;
    const selectedIds = selectionModel.map((id) => {
      const item = dataArray.find((item) => item.id === id || item._id === id);
      return item?._id || id;
    });

    setFormData({
      ...formData,
      receiverIds: selectedIds,
    });
    setSelectedReceivers(selectionModel);
  };

  // Get the appropriate columns and data based on selection
  const getGridColumnsAndData = () => {
    if (formData.receiverIdsType === "user") {
      return {
        columns: NotificationMessagesUsersColumns,
        data: users.map((user, index) => ({
          ...user,
          id: user._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
      };
    } else {
      return {
        columns: NotificationMessagesTechniciansColumns,
        data: technicians.map((tech, index) => ({
          ...tech,
          id: tech._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
      };
    }
  };

  const { columns, data } = formData.receiverIdsType
    ? getGridColumnsAndData()
    : { columns: [], data: [] };

  // Get the message about points
  const getPointMessage = () => {
    if (charCount > 160) {
      return "هذه الرسالة ستحسب 3 نقاط لكل مستلم";
    } else if (charCount > 70) {
      return "هذه الرسالة ستحسب نقطتين لكل مستلم";
    }
    return "";
  };

  return (
    <div className="create-notification">
      <div className="special-header">
        <p className="title">ارسال رسالة</p>
      </div>

      <div className="notification-container">
        <form onSubmit={handleSubmit}>
          <div className="control-group" style={{ flexDirection: "column" }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="message"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                نص الرسالة
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="message"
                type="text"
                notched
                multiline
                minRows={5}
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
              />
            </FormControl>
            <div
              className="char-counter"
              style={{
                textAlign: "right",
                marginTop: "8px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                color={
                  charCount > 160
                    ? "error"
                    : charCount > 70
                    ? "warning"
                    : "initial"
                }
              >
                {getPointMessage()}
              </Typography>
              <Typography variant="body2">{charCount} حرف</Typography>
            </div>
          </div>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel
              htmlFor="receiver-type"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
                textAlign: "right",
              }}
            >
              حدد المستلم
            </InputLabel>
            <Select
              labelId="receiver-type-label"
              id="receiver-type"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={
                formData.receiverType === "custom"
                  ? formData.receiverIdsType === "user"
                    ? "specific-users"
                    : "specific-technicians"
                  : formData.receiverType
              }
              onChange={handleReceiverTypeChange}
            >
              <MenuItem value="all">الجميع</MenuItem>
              <MenuItem value="users">المستخدمين</MenuItem>
              <MenuItem value="technicians">العمال</MenuItem>
              <MenuItem value="specific-users">تحديد مستخدمين</MenuItem>
              <MenuItem value="specific-technicians">تحديد عمال</MenuItem>
            </Select>
          </FormControl>

          <BlueButton
            text={
              submitLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "ارسال"
              )
            }
            size="medium"
            variant="fill"
            type="submit"
            disabled={submitLoading}
          />
        </form>

        {formData.receiverType === "custom" && (
          <div className="left">
            <TextField
              id="outlined-search"
              type="search"
              value={searchTerm}
              placeholder={
                searchTerm.length < 3 && searchTerm.length > 0
                  ? "يرجى إدخال 3 أحرف على الأقل للبحث..."
                  : "ابحث..."
              }
              onChange={handleSearch}
              sx={{ width: "100%", mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <DataGrid
              sx={{ backgroundColor: "white", width: "100%" }}
              columns={columns}
              autoHeight={true}
              rows={data}
              disableVirtualization
              disableColumnFilter
              disableColumnMenu
              loading={loading}
              pagination
              paginationMode="server"
              rowCount={totalCount}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize, page: page - 1 },
                },
              }}
              onPaginationModelChange={(model) => {
                // Handle page change
                if (model.page !== page - 1) {
                  setPage(model.page + 1);
                }

                // Handle page size change
                if (model.pageSize !== pageSize) {
                  setPageSize(model.pageSize);
                  setPage(1); // Reset to first page when changing page size
                }
              }}
              checkboxSelection
              onRowSelectionModelChange={handleSelectionModelChange}
              rowSelectionModel={selectedReceivers}
              getRowClassName={() => "data-grid-row"}
              localeText={{
                MuiTablePagination: {
                  labelRowsPerPage:
                    formData.receiverIdsType === "user"
                      ? "مستخدم في الصفحة"
                      : "فني في الصفحة",
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateMessage;
